import { AlertCircle, Check, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getCompanyStructure } from '../../../redux/actions/structure';
import { apiCreateOrUpdateExpectedScores, apiListExpectedScores } from '../../../redux/api/expectedScoreApi';
import { sortByPtBR } from '../../../helper/sortHelper';
import './CompetencyConfigModal.scss';

const DEFAULT_TAXONOMY_LEVELS = [
  { value: 0, level: 'LEMBRO', description: 'Relembrar informações aprendidas anteriormente' },
  { value: 0.5, level: 'LEMBRO', description: 'Relembrar informações aprendidas anteriormente' },
  { value: 1.0, level: 'LEMBRO', description: 'Relembrar informações aprendidas anteriormente' },
  { value: 1.5, level: 'COMPREENDO', description: 'Compreender o significado e interpretar instruções' },
  { value: 2.0, level: 'COMPREENDO', description: 'Compreender o significado e interpretar instruções' },
  { value: 2.5, level: 'COMPREENDO', description: 'Compreender o significado e interpretar instruções' },
  { value: 3.0, level: 'COMPREENDO', description: 'Compreender o significado e interpretar instruções' },
  { value: 3.5, level: 'COMPREENDO', description: 'Compreender o significado e interpretar instruções' },
  { value: 4.0, level: 'APLICO', description: 'Aplicar conhecimento em situações familiares' },
  { value: 4.5, level: 'APLICO', description: 'Aplicar conhecimento em situações familiares' },
  { value: 5.0, level: 'APLICO', description: 'Aplicar conhecimento em situações familiares' },
  { value: 5.5, level: 'APLICO', description: 'Aplicar conhecimento em situações familiares' },
  { value: 6.0, level: 'ANALISO', description: 'Dividir informações em partes para explorar compreensões' },
  { value: 6.5, level: 'ANALISO', description: 'Dividir informações em partes para explorar compreensões' },
  { value: 7.0, level: 'ANALISO', description: 'Dividir informações em partes para explorar compreensões' },
  { value: 7.5, level: 'ANALISO', description: 'Dividir informações em partes para explorar compreensões' },
  { value: 8.0, level: 'AVALIO', description: 'Justificar uma decisão ou curso de ação' },
  { value: 8.5, level: 'AVALIO', description: 'Justificar uma decisão ou curso de ação' },
  { value: 9.0, level: 'AVALIO', description: 'Justificar uma decisão ou curso de ação' },
  { value: 9.5, level: 'AVALIO', description: 'Justificar uma decisão ou curso de ação' },
  { value: 10, level: 'CRIO', description: 'Gerar novas ideias, produtos ou formas de ver as coisas' }
];

const BLOOM_MAPPING = {
  bloom_remember: { level: 'LEMBRO', values: [0, 0.5, 1.0, 1.5, 2.0, 2.5] },
  bloom_understand: { level: 'COMPREENDO', values: [3.0, 3.5] },
  bloom_apply: { level: 'APLICO', values: [4.0, 4.5, 5.0, 5.5] },
  bloom_analyse: { level: 'ANALISO', values: [6.0, 6.5, 7.0, 7.5] },
  bloom_evaluate: { level: 'AVALIO', values: [8.0, 8.5, 9.0, 9.5] },
  bloom_create: { level: 'CRIO', values: [10] }
};

const createTaxonomyLevels = (competencyInfos) => {
  if (!competencyInfos) return DEFAULT_TAXONOMY_LEVELS;

  const taxonomyLevels = [];
  Object.entries(BLOOM_MAPPING).forEach(([bloomKey, config]) => {
    const description = competencyInfos[bloomKey];
    if (description) {
      config.values.forEach(value =>
        taxonomyLevels.push({ value, level: config.level, description })
      );
    }
  });

  return taxonomyLevels.length > 0 ? taxonomyLevels : DEFAULT_TAXONOMY_LEVELS;
};

const CompetencyConfigModal = ({ isOpen, onClose, competencyName, competencyDescription, topicId, competencyInfos }) => {
  const dispatch = useDispatch();
  const structure = useSelector((state) => state?.structure);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [expectedScore, setExpectedScore] = useState(5);
  const [showModal, setShowModal] = useState(false);
  const [teamRoles, setTeamRoles] = useState([]);
  const [expectedScores, setExpectedScores] = useState([]);
  const [selectOpen, setSelectOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingScores, setIsLoadingScores] = useState(false);

  const taxonomyLevels = useMemo(() => createTaxonomyLevels(competencyInfos), [competencyInfos]);

  useEffect(() => {
    if (!selectOpen) return;
    const handleClickOutside = (e) => {
      const select = document.querySelector('.cc-select-custom');
      if (select && !select.contains(e.target)) setSelectOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectOpen]);

  useEffect(() => {
    if (!isOpen) {
      setShowModal(false);
      return;
    }

    dispatch(getCompanyStructure());
    setSelectedTeam('');
    setSelectedRoles([]);
    setExpectedScore(5);
    setTimeout(() => setShowModal(true), 10);

    if (topicId) {
      setIsLoadingScores(true);
      apiListExpectedScores({ topic_id: topicId })
        .then(data => setExpectedScores(data?.expected_scores || []))
        .catch(() => setExpectedScores([]))
        .finally(() => setIsLoadingScores(false));
    } else {
      setExpectedScores([]);
    }
  }, [isOpen, dispatch, topicId]);

  const getTeamStatus = useCallback((unit) => {
    if (!unit) return { configured: 0, total: 0, isComplete: false };

    const reduxUnit = Array.isArray(structure?.data)
      ? structure.data.find(u => String(u.id) === String(unit.id) || u.name === unit.name)
      : null;

    const roles = Array.isArray(reduxUnit?.roles) ? reduxUnit.roles : [];
    const configuredRoles = roles.filter(role => {
      const roleName = typeof role === 'object' ? role.name : role;
      return expectedScores.some(es => String(es.department) === String(unit.name) && String(es.role) === String(roleName));
    }).length;

    return {
      configured: configuredRoles,
      total: roles.length,
      isComplete: roles.length > 0 && configuredRoles === roles.length
    };
  }, [structure, expectedScores]);

  const teams = useMemo(() =>
    Array.isArray(structure?.data)
      ? sortByPtBR(
        structure.data.map((team, idx) => ({
          id: team.id || String(idx),
          name: team.name,
          roles: team.roles || []
        })),
        (team) => team.name
      )
      : [],
    [structure]
  );

  const handleRoleToggle = useCallback((roleId) => {
    setSelectedRoles(prev => {
      if (prev.includes(roleId)) {
        return prev.filter(id => id !== roleId);
      }

      const role = teamRoles.find(r => r.id === roleId);
      if (prev.length === 0) {
        setExpectedScore(typeof role?.expectedScore === 'number' ? role.expectedScore : 0);
      }
      return [...prev, roleId];
    });
  }, [teamRoles]);

  const handleSave = useCallback(async () => {
    if (!selectedTeam || selectedRoles.length === 0) return;

    setIsSaving(true);
    const selectedTeamObj = teams.find(t => String(t.id) === String(selectedTeam));
    const departmentName = selectedTeamObj ? selectedTeamObj.name : selectedTeam;

    const payload = {
      expected_scores: selectedRoles.map(roleId => ({
        topic_id: topicId,
        department: departmentName,
        role: roleId,
        expected_score: expectedScore
      }))
    };

    try {
      await apiCreateOrUpdateExpectedScores(payload);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar configuração.');
    } finally {
      setIsSaving(false);
    }
  }, [selectedTeam, selectedRoles, expectedScore, topicId, onClose, teams]);

  const handleTeamChange = useCallback((e) => {
    const teamId = e.target.value;
    setSelectedTeam(teamId);
    setSelectedRoles([]);
    setExpectedScore(0);

    const selected = teams.find(t => String(t.id) === String(teamId));
    const roles = Array.isArray(selected?.roles)
      ? selected.roles.map((role, i) => {
        const roleId = typeof role === 'string' ? role : role.id || `${selected?.id || teamId}-${i}`;
        const roleName = typeof role === 'string' ? role : role.name || '';
        const found = expectedScores.find(es => String(es.department) === String(selected?.name) && String(es.role) === String(roleName));
        return {
          id: roleId,
          name: roleName,
          isConfigured: !!found,
          expectedScore: found ? found.expected_score : undefined
        };
      })
      : [];
    setTeamRoles(sortByPtBR(roles, (r) => r.name));
  }, [teams, expectedScores]);

  const taxonomyValues = useMemo(() => taxonomyLevels.map(level => level.value), [taxonomyLevels]);
  const selectedTeamData = useMemo(() => selectedTeam ? teams.find(team => String(team.id) === String(selectedTeam)) : null, [selectedTeam, teams]);
  const currentTaxonomy = useMemo(() => taxonomyLevels.find(level => level.value === expectedScore) || taxonomyLevels[0], [expectedScore, taxonomyLevels]);

  const handleSliderChange = useCallback((e) => {
    const value = parseFloat(e.target.value);
    const closestValue = taxonomyValues.reduce((prev, curr) =>
      Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
    );
    setExpectedScore(closestValue);
  }, [taxonomyValues]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target.classList.contains('cc-modal-overlay')) onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className={`cc-modal-overlay${showModal ? ' cc-modal-overlay--open' : ''}`} onClick={handleOverlayClick}>
      <div className={`cc-modal${showModal ? ' cc-modal--open' : ''}`}>
        <div className="cc-modal-header">
          <span className="cc-modal-title">Configurar nota esperada</span>
          <button className="cc-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="cc-modal-body">
          <div className="cc-modal-competency">
            <span className="cc-modal-competency-name">{competencyName}</span>
            {competencyDescription && (
              <span className="cc-modal-competency-desc">{competencyDescription}</span>
            )}
          </div>
          <div className="cc-modal-section">
            <label className="cc-label"><Users className="cc-icon" /> Selecionar Time</label>
            <div className={`cc-select-custom${selectOpen ? ' cc-select-open' : ''}`}>
              <div className="cc-select-selected" onClick={() => setSelectOpen(o => !o)}>
                {isLoadingScores ? (
                  <span className="cc-select-placeholder">Carregando configurações...</span>
                ) : selectedTeam ? (
                  (() => {
                    const team = teams.find(t => String(t.id) === String(selectedTeam));
                    const status = getTeamStatus(team);
                    return (
                      <div className="cc-select-row">
                        <span className="cc-select-team-name">{team?.name}</span>
                        <span className="cc-select-team-status">
                          {status.configured}/{status.total} cargos
                          {status.isComplete ?
                            <Check size={16} className="cc-select-icon-ok" /> :
                            <AlertCircle size={16} className="cc-select-icon-alert" />
                          }
                        </span>
                      </div>
                    );
                  })()
                ) : (
                  <span className="cc-select-placeholder">Selecione um time</span>
                )}
                <span className="cc-select-arrow" />
              </div>
              {isLoadingScores ? null : (
                <div className="cc-select-dropdown" style={{ display: selectOpen ? 'block' : 'none' }}>
                  {teams.map(team => {
                    const status = getTeamStatus(team);
                    return (
                      <div
                        key={team.id}
                        className={`cc-select-option${String(selectedTeam) === String(team.id) ? ' cc-select-option--selected' : ''}`}
                        onClick={() => {
                          handleTeamChange({ target: { value: team.id } });
                          setSelectOpen(false);
                        }}
                      >
                        <div className="cc-select-row cc-select-row--dropdown">
                          <span className="cc-select-team-name">{team.name}</span>
                          <div className="cc-select-team-status-group">
                            <span className="cc-select-team-status-count">{status.configured}/{status.total} cargos</span>
                            {status.isComplete ? (
                              <Check className="cc-select-team-status-icon cc-select-team-status-icon--ok" />
                            ) : (
                              <AlertCircle className="cc-select-team-status-icon cc-select-team-status-icon--alert" />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          {selectedTeam && teamRoles.length > 0 && (
            <div className="cc-modal-section">
              <div className="cc-label">Selecionar Cargos ({selectedRoles.length} selecionados)</div>
              <div className="cc-roles-list">
                {teamRoles.map(role => (
                  <label key={role.id} className={`cc-role ${selectedRoles.includes(role.id) ? 'cc-role-selected' : ''} ${role.isConfigured ? 'cc-role-configured' : ''}`}>
                    <div>
                      <input
                        type="checkbox"
                        checked={selectedRoles.includes(role.id)}
                        onChange={() => handleRoleToggle(role.id)}
                      />
                      <span>{role.name}</span>
                    </div>
                    {role.isConfigured ? (
                      <span className="cc-role-status cc-role-status-configured">
                        <Check className="cc-icon-sm" /> Nota: {role.expectedScore}
                      </span>
                    ) : (
                      <span className="cc-role-status cc-role-status-alert">
                        <AlertCircle className="cc-icon-sm" /> Não configurado
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}
          {selectedRoles.length > 0 && (
            <div className="cc-modal-section">
              <div className="cc-label">Nota Esperada: <b>{expectedScore}</b></div>
              <div className="cc-taxonomy">
                <span className="cc-taxonomy-level">{currentTaxonomy.level}</span>
                <span className="cc-taxonomy-desc">{currentTaxonomy.description}</span>
              </div>
              <input
                type="range"
                min={0}
                max={10}
                step={0.5}
                value={expectedScore}
                onChange={handleSliderChange}
                className="cc-slider"
                disabled={selectedRoles.length === 0}
                style={{
                  '--cc-slider-progress': `${((expectedScore - 0) / (10 - 0)) * 100}%`
                }}
              />
              <div className="cc-slider-ticks">
                {taxonomyValues.map(value => (
                  <span key={value} className="cc-slider-tick">{value}</span>
                ))}
              </div>
            </div>
          )}
          {!selectedTeam && (
            <div className="cc-modal-message">
              <Users className="cc-icon-lg" />
              <span>Selecione um time para configurar as notas esperadas</span>
            </div>
          )}
          {selectedTeam && selectedRoles.length === 0 && selectedTeamData && (
            <div className="cc-modal-message">
              <AlertCircle className="cc-icon-lg cc-icon-alert" />
              <span>Selecione pelo menos um cargo para configurar a nota esperada</span>
            </div>
          )}
        </div>
        <div className="cc-modal-footer">
          <button className="cc-btn cc-btn-outline" onClick={onClose} disabled={isSaving}>Cancelar</button>
          <button className="cc-btn cc-btn-primary" onClick={handleSave} disabled={selectedRoles.length === 0 || isSaving}>
            {isSaving ? 'Salvando...' : 'Salvar Configuração'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CompetencyConfigModal;
